import { ScrollShadow, Tab, Tabs } from '@heroui/react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useFormContext } from 'react-hook-form';
import { BiReset } from 'react-icons/bi';
import { BsImage } from 'react-icons/bs';
import { FaSave } from 'react-icons/fa';
import { GoSync } from 'react-icons/go';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAssetActions } from '@/components/assets';
import {
  getAiConfigFormDefaults,
  getAiConfigSubmitValue,
  mergeAiConfigForSubmit,
} from '@/components/station/ai-config-form';
import { phaseTimersToApi } from '@/components/station/phase-timers-utils';
import { type IStationSchema, stationSchema } from '@/components/station/schema';
import { ROUTES } from '@/config/routes';
import { FormError } from '@/core/molecules';
import { AsyncAutoComplete, Button, ConfirmTrigger, cn, Dialog, FormSelect, toaster } from '@/core/ui';
import {
  AdminStationDocument,
  type AdminStationQuery,
  type AdminStationQueryVariables,
  UpdateStationDocument,
  type UpdateStationInput,
} from '@/graphql/gql/graphql';
import { type ApolloClient, useMutation } from '@/libs';
import { useAsset } from '@/store/asset';
import { useStation } from '@/store/station';

type ToolbarProps = {
  disabled: boolean;
  loading: boolean;
  refetch: (
    variables?: Partial<AdminStationQueryVariables> | undefined,
  ) => Promise<ApolloClient.QueryResult<AdminStationQuery>>;
};

export const Top = ({ disabled, loading, refetch }: ToolbarProps) => {
  const { id } = useParams<{ id: string }>();
  const preview = useStation.use.preview();
  const setPreview = useStation.use.setPreview();
  const setStationId = useStation.use.setStationId();
  const form = useFormContext<IStationSchema>();
  const navigate = useNavigate();
  const setAssetModal = useAsset.use.setAssetModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRefetching, setIsRefetching] = useState(false);
  const { resetAssets } = useAssetActions();
  const [currentData, setCurrentData] = useState<IStationSchema>();
  const stationId = useStation.use.stationId();
  const station = useStation.use.station();
  const originalAssets = useAsset.use.originalAssets();
  const [updateStation, { loading: updateLoading }] = useMutation(UpdateStationDocument, {
    refetchQueries: [AdminStationDocument, 'StationData'],
  });
  const isEmpty = !id || id === 'null';

  const hasChangedValue = (value: unknown): boolean => {
    if (typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.some((item) => hasChangedValue(item));
    if (value && typeof value === 'object') return Object.values(value).some((item) => hasChangedValue(item));
    return false;
  };

  // Sync input value when stationId changes from external sources
  useEffect(() => {
    setStationId(Number(id));
  }, [id]);

  useEffect(() => {
    setPreview(searchParams.get('preview') === 'true');
  }, [searchParams]);

  const handleDraft = (value: boolean) => {
    setPreview(value);
    searchParams.set('preview', value.toString());
    setSearchParams(searchParams);
  };

  const handleReset = async () => {
    await resetAssets({
      assetIds:
        form
          .getValues('assets')
          ?.filter((a) => !a.linked)
          .map((a) => a.id) || [],
      resetForm: () => form.reset(),
      resetMapping: true,
      oldAssets: [...originalAssets],
    });
  };

  const changeStation = (newStationId?: number) => {
    const id = newStationId ?? stationId;
    setStationId(id);
    navigate(ROUTES.CONTENT.EDIT.STATION.replace(':id', `${id}?preview=${preview}`), {
      replace: true,
    });
    refetch({ id });
  };

  const handleChangeStationId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStationId(Number(e.target.value));
  };

  const handleRefetch = () => {
    setIsRefetching(true);
    refetch({ id: stationId })
      .then(() => setIsRefetching(false))
      .catch(() => setIsRefetching(false));
  };

  const onSubmit: SubmitHandler<IStationSchema> = (userInput: IStationSchema) => {
    const stationId = id;
    let updates = { id: stationId } as UpdateStationInput;

    if (!stationId) {
      toaster.error('Station ID is required');
      return;
    }
    const dirtyFields = Object.keys(form.formState.dirtyFields).filter((key) => {
      const value = form.formState.dirtyFields[key as keyof typeof form.formState.dirtyFields];
      return hasChangedValue(value);
    });
    const touchedFields = Object.keys(form.formState.touchedFields);
    const changedFields = Array.from(new Set([...dirtyFields, ...touchedFields]));
    const dirtyData = changedFields.reduce((acc, key) => {
      const keyForm = key as keyof IStationSchema;
      acc[keyForm] = userInput[keyForm] as never;
      return acc;
    }, {} as IStationSchema);

    if (Object.keys(dirtyData).length === 0) {
      toaster.info('No changes are different from the original station');
      return;
    }

    updates = Object.assign(updates, dirtyData);
    if (dirtyData.assets) {
      updates.assets = dirtyData.assets.map((asset) => ({
        id: asset.picture?.id as string,
        index: asset.index ?? 0,
        deleted: asset.deleted ?? false,
        choiceLabel: asset.choiceLabel ?? undefined,
        typeId: asset.type,
      }));
    }

    if (Object.hasOwn(dirtyData, 'enabled')) {
      (updates as any).enabled = Number(dirtyData.enabled ?? 0);
    }

    if (Object.hasOwn(dirtyData, 'entitlementIds')) {
      updates.entitlementIds = dirtyData.entitlementIds?.map(Number);
    }
    if (Object.hasOwn(dirtyData, 'topicIds')) {
      updates.topicIds = dirtyData.topicIds?.map(Number);
    }

    if (Object.hasOwn(dirtyData, 'marks')) {
      updates.marks = dirtyData.marks
        .flatMap((mark) =>
          mark.choices.map((choice) => ({
            header: mark.header,
            name: choice.name,
            index: choice.index,
            weight: choice.weight ?? 1,
            excludeFromAi: choice.excludeFromAi,
          })),
        )
        .sort((a, b) => a.index - b.index);
    }

    if (Object.hasOwn(dirtyData, 'picture')) {
      updates.pictureId = dirtyData.picture ? parseInt(dirtyData.picture?.id as string, 10) : null;
      delete (updates as any).picture;
    }
    if (Object.hasOwn(dirtyData, 'aiConfig')) {
      try {
        const baseline = form.formState.defaultValues?.aiConfig ?? getAiConfigFormDefaults(station?.aiConfig);
        const merged = mergeAiConfigForSubmit(form.getValues('aiConfig'), baseline);
        updates.aiConfig = getAiConfigSubmitValue(merged);
      } catch (error) {
        toaster.error(error instanceof Error ? error.message : 'Invalid AI config');
        return;
      }
    }

    if (Object.hasOwn(dirtyData, 'phaseTimers') && dirtyData.phaseTimers) {
      updates.phaseTimers = phaseTimersToApi(dirtyData.phaseTimers);
    }

    updateStation({ variables: { input: updates } })
      .then((res) => {
        if (res.data?.admin?.updateStation) {
          toaster.success('Station updated successfully');
        } else {
          toaster.error('Failed to update station');
        }
      })
      .catch((err) => {
        toaster.error(err.message || 'Failed to update station');
      });
  };

  const isDisabled =
    disabled || !form.formState.isValid || Object.keys(form.formState.dirtyFields).length === 0;

  return (
    <div className={cn('flex justify-between items-center p-3 w-full')}>
      <div className='flex items-center gap-4'>
        <div className='flex items-center gap-2 bg-default-50 rounded-lg p-1.5 border border-default-200'>
          <div className='flex items-center gap-y-2'>
            <span className='text-sm font-medium text-default-600'># ID:</span>
          </div>
          <div className='flex items-center gap-2'>
            <input
              type='number'
              value={stationId ? stationId.toString() : ''}
              onChange={handleChangeStationId}
              className='w-32 px-2 py-1 text-sm border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
              placeholder={stationId.toString()}
              min='1'
            />
            <Button
              size='sm'
              variant='solid'
              color='primary'
              className='px-3 text-xs rounded-md'
              onPress={() => changeStation()}
              isDisabled={!stationId || stationId === Number(id)}
            >
              Apply
            </Button>
          </div>
          <AsyncAutoComplete
            showAvatar={false}
            queryKey='stations'
            groupBy='options'
            size='sm'
            apiSearch={false}
            isVirtualized={false}
            variant='faded'
            className='w-40'
            classNames={{
              popoverContent: 'w-84',
            }}
            placeholder='Search..'
            renderItem={(item) => <span className='text-small'>{item.label}</span>}
            onSelectionChange={(key) => {
              if (!key) return;
              const numId = Number(key);
              changeStation(numId);
            }}
          />
          <Button
            size='sm'
            variant='solid'
            color='warning'
            className='min-w-8 text-xs rounded-md'
            onPress={handleRefetch}
          >
            <GoSync
              className={cn('size-4 stroke-[1px] text-background', {
                'animate-spin': isRefetching || loading,
              })}
            />
          </Button>
        </div>
        <Tabs
          disabledKeys={disabled || isEmpty ? ['edit', 'preview'] : []}
          onSelectionChange={(key) => handleDraft(key === 'preview')}
          selectedKey={preview ? 'preview' : 'edit'}
        >
          <Tab title='Edit' key='edit' />
          <Tab title='Preview' key='preview' />
        </Tabs>

        {!isEmpty && preview ? null : !isEmpty ? (
          <>
            {/* <div className='flex items-center gap-x-6 bg-default-50 rounded-lg py-1 px-4 border border-default-200'> */}
            <FormSelect
              isDisabled={disabled}
              id='enabled'
              name='enabled'
              variant='faded'
              className='w-26'
              // size='sm'
              control={form.control}
              options={[
                { label: 'Visible', key: '1' },
                { label: 'Hidden', key: '0' },
                { label: 'Admin Only', key: '2' },
              ]}
            />
            {/* </div> */}

            <Dialog
              size='2xl'
              radius='sm'
              title='Form JSON'
              trigger={(onOpen) => (
                <Button
                  color='default'
                  // size='sm'
                  onPress={() => {
                    setCurrentData(form.getValues());
                    onOpen();
                  }}
                >
                  JSON
                </Button>
              )}
            >
              <ScrollShadow className='bg-default-50 max-h-[75dvh] border border-default-200 rounded-lg p-4 text-xs'>
                <pre>{JSON.stringify(currentData, null, 2)}</pre>
              </ScrollShadow>
            </Dialog>

            <Button
              color='default'
              // size='sm'
              startContent={<BsImage className='size-4' />}
              onPress={() => {
                setAssetModal(true, 'Question');
              }}
            >
              Manage
            </Button>
            {!preview && !form.formState.isValid && !isEmpty && (
              <Dialog
                size='2xl'
                radius='sm'
                title='Form Errors'
                trigger={(onOpen) => (
                  <Button variant='flat' color='warning' size='sm' onPress={onOpen}>
                    Errors
                  </Button>
                )}
              >
                <FormErrors />
              </Dialog>
            )}
          </>
        ) : null}
      </div>

      <div className='flex items-center gap-2'>
        {form.formState.dirtyFields && Object.keys(form.formState.dirtyFields).length > 0 && (
          <Button
            variant='light'
            color='warning'
            className='gap-2 disabled:cursor-not-allowed'
            onPress={handleReset}
          >
            <BiReset className='size-4' />
            Reset
          </Button>
        )}

        <ConfirmTrigger
          type='warn'
          isLoading={updateLoading}
          title='Save Station'
          description={
            <div className='flex flex-col gap-2'>
              <p>Are you sure you want to proceed?</p>
              {(() => {
                const dirtyFields = Object.keys(form.formState.dirtyFields).filter((key) => {
                  const value = form.formState.dirtyFields[key as keyof typeof form.formState.dirtyFields];
                  return hasChangedValue(value);
                });
                const touchedFields = Object.keys(form.formState.touchedFields);
                const changedFields = Array.from(new Set([...dirtyFields, ...touchedFields]));
                return changedFields.length > 0 ? (
                  <div className='bg-warning-50 border border-warning-200 rounded p-2 mt-2'>
                    <p className='text-xs text-warning-700 font-semibold mb-1'>Changed Fields:</p>
                    <ul className='list-disc list-inside text-xs text-warning-800'>
                      {changedFields.map((field) => (
                        <li key={field}>
                          <code>{field}</code>
                          {/* <span className='ml-2 text-default-500 break-all'>
                            {JSON.stringify(changedFields[field as keyof typeof changedFields])}
                          </span> */}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null;
              })()}
            </div>
          }
          onConfirm={form.handleSubmit(onSubmit)}
          trigger={({ setIsOpen }) => (
            <Button
              startContent={<FaSave className='w-4 h-4' />}
              className='text-background'
              color='success'
              isLoading={updateLoading}
              variant='solid'
              onPress={() => setIsOpen(true)}
              isDisabled={isDisabled || updateLoading}
            >
              Save
            </Button>
          )}
        />
        {/* {!isEmpty && <Actions />} */}
      </div>
    </div>
  );
};

const FormErrors = () => {
  const { getValues } = useFormContext();
  if (!stationSchema) return null;

  const result = stationSchema.safeParse(getValues());

  return <FormError result={result} />;
};
